/**
 * 分镜 API 服务
 * 对接后端 /api/v1/storyboards
 */
import apiClient from './client';
import { storyboardBoardApi } from './storyboard-board';
import type { StoryboardExtractJobResponse } from '@/types/ai';
import type { Storyboard, CreateStoryboardDto, UpdateStoryboardDto } from '@/types';
import type { ScreenplayScene } from '@/types/screenplay';
import type {
  DialogueLinkedElementsPayload,
  DialogueSyncItem,
  DialogueSyncResponse,
} from './storyboard-resources';

interface ApiErrorWithCode {
  code: number;
}

const hasErrorCode = (error: unknown): error is ApiErrorWithCode => {
  if (typeof error !== 'object' || error === null || !('code' in error)) {
    return false;
  }

  return typeof error.code === 'number';
};

interface StoryboardItemApiResponse {
  itemId?: string;
  item_id?: string;
  itemType?: number;
  item_type?: number;
  elementTagId?: string | null;
  element_tag_id?: string | null;
  resourceId?: string | null;
  resource_id?: string | null;
  elementName?: string | null;
  element_name?: string | null;
  tagLabel?: string | null;
  tag_label?: string | null;
  coverUrl?: string | null;
  cover_url?: string | null;
  resourceType?: number | null;
  resource_type?: number | null;
  sourceResourceId?: string | null;
  source_resource_id?: string | null;
  editAction?: string | null;
  edit_action?: string | null;
  versionSeq?: number | null;
  version_seq?: number | null;
  actionDescription?: string | null;
  action_description?: string | null;
  spatialPosition?: string | null;
  spatial_position?: string | null;
  elementType?: number | null;
  element_type?: number | null;
  elementId?: string | null;
  element_id?: string | null;
  isVisible?: boolean;
  is_visible?: boolean;
  displayOrder?: number;
  display_order?: number;
  zIndex?: number;
  z_index?: number;
  metadata?: Record<string, unknown>;
  createdAt?: string | null;
  created_at?: string | null;
  updatedAt?: string | null;
  updated_at?: string | null;
}

interface StoryboardApiResponse {
  storyboardId?: string;
  storyboard_id?: string;
  id?: string;
  projectId?: string;
  project_id?: string;
  sceneId?: string | null;
  scene_id?: string | null;
  title?: string;
  description?: string | null;
  shootingDescription?: string | null;
  shooting_description?: string | null; // 后端蛇形命名
  thumbnailUrl?: string | null;
  thumbnail_url?: string | null;
  orderIndex?: number | string | null;
  order_index?: number | string | null;
  startTime?: number | string | null;
  start_time?: number | string | null;
  endTime?: number | string | null;
  end_time?: number | string | null;
  estimatedDuration?: number | string | null; // 预估时长
  estimated_duration?: number | string | null; // 后端蛇形命名
  actualDuration?: number | string | null; // 实际时长
  actual_duration?: number | string | null; // 后端蛇形命名
  transitionType?: string | null;
  transition_type?: string | null;
  transitionDuration?: number | string | null;
  transition_duration?: number | string | null;
  items?: StoryboardItemApiResponse[] | null;
  metadata?: Storyboard['metadata'] | null;
  meta_data?: Storyboard['metadata'] | null;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

interface StoryboardListResponse {
  items: StoryboardApiResponse[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface StoryboardListParams {
  page?: number;
  pageSize?: number;
  includeItems?: boolean;
  startIndex?: number;
  endIndex?: number;
  anchorStoryboardId?: string;
}

export interface PaginatedStoryboardList {
  items: Storyboard[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface StoryboardBatchSaveUpdatePayload {
  storyboardId: string;
  storyboard: Pick<
    UpdateStoryboardDto,
    | 'sceneId'
    | 'title'
    | 'description'
    | 'shootingDescription'
    | 'estimatedDuration'
    | 'customFields'
  >;
  dialogues?: DialogueSyncItem[];
  linkedElements?: DialogueLinkedElementsPayload;
  syncDialogues?: boolean;
  digest?: string;
}

export interface StoryboardBatchSavePayload {
  projectId: string;
  updates: StoryboardBatchSaveUpdatePayload[];
  orderedStoryboardIds: string[];
}

export interface StoryboardBatchSaveItem {
  storyboardId: string;
  digest?: string | null;
  dialogueSync?: DialogueSyncResponse | null;
}

export interface StoryboardBatchSaveResponse {
  items: StoryboardBatchSaveItem[];
  orderedStoryboardIds: string[];
  createdCount: number;
  updatedCount: number;
}

const toNumber = (value: unknown, fallback?: number) => {
  if (value === null || value === undefined || value === '') return fallback;
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const normalizeDescription = (value: unknown) => {
  if (typeof value !== 'string') return '';
  return value.trim() ? value : '';
};

const normalizeStoryboardItem = (
  raw: StoryboardItemApiResponse
): NonNullable<Storyboard['items']>[number] => {
  const itemType = (raw.itemType ?? raw.item_type ?? 2) as 1 | 2;
  return {
    itemId: raw.itemId ?? raw.item_id ?? '',
    itemType,
    elementTagId: raw.elementTagId ?? raw.element_tag_id ?? undefined,
    resourceId: raw.resourceId ?? raw.resource_id ?? undefined,
    elementType: (raw.elementType ?? raw.element_type ?? undefined) as 1 | 2 | 3 | undefined,
    elementId: raw.elementId ?? raw.element_id ?? undefined,
    elementName: raw.elementName ?? raw.element_name ?? '',
    tagLabel: raw.tagLabel ?? raw.tag_label ?? undefined,
    coverUrl: raw.coverUrl ?? raw.cover_url ?? undefined,
    resourceType: raw.resourceType ?? raw.resource_type ?? undefined,
    sourceResourceId: (() => {
      const value = raw.sourceResourceId ?? raw.source_resource_id;
      return typeof value === 'string' && value.trim() ? value.trim() : undefined;
    })(),
    editAction: (() => {
      const value = raw.editAction ?? raw.edit_action;
      return typeof value === 'string' && value.trim() ? value.trim() : undefined;
    })(),
    versionSeq: (() => {
      const rawSeq = raw.versionSeq ?? raw.version_seq;
      return typeof rawSeq === 'number' && Number.isFinite(rawSeq) && rawSeq > 0
        ? rawSeq
        : undefined;
    })(),
    actionDescription: raw.actionDescription ?? raw.action_description ?? undefined,
    spatialPosition: raw.spatialPosition ?? raw.spatial_position ?? undefined,
    isVisible: raw.isVisible ?? raw.is_visible ?? true,
    displayOrder: raw.displayOrder ?? raw.display_order ?? 0,
    zIndex: raw.zIndex ?? raw.z_index ?? 0,
    metadata: raw.metadata ?? undefined,
    createdAt: raw.createdAt ?? raw.created_at ?? undefined,
    updatedAt: raw.updatedAt ?? raw.updated_at ?? undefined,
  };
};

const normalizeStoryboard = (raw: StoryboardApiResponse): Storyboard => {
  const id = raw.storyboardId ?? raw.storyboard_id ?? raw.id ?? '';
  const projectId = raw.projectId ?? raw.project_id ?? '';

  return {
    id,
    projectId,
    sceneId: raw.sceneId ?? raw.scene_id ?? undefined,
    title: raw.title || '',
    description: normalizeDescription(raw.description),
    shootingDescription: normalizeDescription(raw.shootingDescription ?? raw.shooting_description),
    thumbnailUrl: raw.thumbnailUrl ?? raw.thumbnail_url ?? undefined,
    orderIndex: toNumber(raw.orderIndex ?? raw.order_index, 0) ?? 0,
    startTime:
      raw.startTime != null || raw.start_time != null
        ? (toNumber(raw.startTime ?? raw.start_time, 0) ?? 0)
        : undefined,
    endTime:
      raw.endTime != null || raw.end_time != null
        ? (toNumber(raw.endTime ?? raw.end_time, 0) ?? 0)
        : undefined,
    estimatedDuration: toNumber(raw.estimatedDuration ?? raw.estimated_duration),
    actualDuration: toNumber(raw.actualDuration ?? raw.actual_duration),
    transitionType: (raw.transitionType ??
      raw.transition_type ??
      undefined) as Storyboard['transitionType'],
    transitionDuration: toNumber(raw.transitionDuration ?? raw.transition_duration),
    items: raw.items?.map(normalizeStoryboardItem) ?? undefined,
    metadata: raw.metadata ?? raw.meta_data ?? undefined,
    createdAt: raw.createdAt ?? raw.created_at ?? new Date().toISOString(),
    updatedAt: raw.updatedAt ?? raw.updated_at ?? new Date().toISOString(),
  };
};

const normalizeStoryboards = (items: StoryboardApiResponse[]) => items.map(normalizeStoryboard);

const buildDescriptionPayload = (value?: string) => (value && value.trim() ? value : ' ');

const fetchStoryboardPage = async (
  projectId: string,
  {
    page = 1,
    pageSize = 100,
    includeItems = false,
    startIndex,
    endIndex,
    anchorStoryboardId,
  }: StoryboardListParams = {},
  signal?: AbortSignal
): Promise<PaginatedStoryboardList> => {
  const params: Record<string, unknown> = {
    projectId: projectId,
    includeItems,
  };
  if (typeof startIndex === 'number' && typeof endIndex === 'number') {
    params.startIndex = startIndex;
    params.endIndex = endIndex;
  } else {
    params.page = page;
    params.pageSize = pageSize;
  }
  if (anchorStoryboardId) {
    params.anchorStoryboardId = anchorStoryboardId;
  }
  const response = (await apiClient.get('/storyboards', {
    params,
    ...(signal ? { signal } : {}),
  })) as StoryboardListResponse;
  return {
    items: normalizeStoryboards(response.items || []),
    total: response.total,
    page: response.page,
    pageSize: response.pageSize,
    totalPages: response.totalPages,
  };
};

const reorderStoryboards = async (projectId: string, orderedIds: string[]) =>
  storyboardBoardApi.reorder(projectId, orderedIds);

export const storyboardApi = {
  async listByProjectId(
    projectId: string,
    params?: StoryboardListParams
  ): Promise<PaginatedStoryboardList> {
    return fetchStoryboardPage(projectId, params);
  },

  async listByWindow(
    projectId: string,
    startIndex: number,
    endIndex: number,
    includeItems = false
  ): Promise<PaginatedStoryboardList> {
    return fetchStoryboardPage(projectId, {
      page: 1,
      pageSize: Math.max(1, endIndex - startIndex + 1),
      includeItems,
      startIndex,
      endIndex,
    });
  },

  async listByAnchor(
    projectId: string,
    anchorStoryboardId: string,
    pageSize = 100,
    includeItems = false
  ): Promise<PaginatedStoryboardList> {
    return fetchStoryboardPage(projectId, {
      page: 1,
      pageSize,
      includeItems,
      anchorStoryboardId,
    });
  },

  // 获取项目的所有分镜
  async getByProjectId(
    projectId: string,
    params?: Pick<StoryboardListParams, 'includeItems' | 'pageSize'>,
    signal?: AbortSignal
  ): Promise<Storyboard[]> {
    const includeItems = params?.includeItems ?? false;
    const pageSize = params?.pageSize ?? 100;
    signal?.throwIfAborted();
    const firstPage = await fetchStoryboardPage(
      projectId,
      {
        page: 1,
        pageSize,
        includeItems,
      },
      signal
    );
    const allItems = [...firstPage.items];

    for (let page = 2; page <= firstPage.totalPages; page += 1) {
      signal?.throwIfAborted();
      const nextPage = await fetchStoryboardPage(
        projectId,
        {
          page,
          pageSize: firstPage.pageSize,
          includeItems,
        },
        signal
      );
      allItems.push(...nextPage.items);
    }

    signal?.throwIfAborted();
    return allItems;
  },

  // 获取单个分镜
  async getById(id: string): Promise<Storyboard | null> {
    const response = (await apiClient.get(`/storyboards/${id}`, {
      params: { includeItems: true },
    })) as StoryboardApiResponse;
    return normalizeStoryboard(response);
  },

  // 创建分镜
  async create(data: CreateStoryboardDto): Promise<Storyboard> {
    const payload = {
      project_id: data.projectId,
      scene_id: data.sceneId,
      title: data.title,
      description: buildDescriptionPayload(data.description),
      shooting_description: data.shootingDescription,
      ...(data.customFields !== undefined ? { custom_fields: data.customFields } : {}),
      start_time: data.startTime,
      end_time: data.endTime,
    };

    const response = (await apiClient.post('/storyboards', payload)) as StoryboardApiResponse;
    const created = normalizeStoryboard(response);

    if (typeof data.orderIndex === 'number') {
      const list = await storyboardApi.getByProjectId(data.projectId);
      const orderedIds = list.map((s) => s.id).filter((id) => id !== created.id);
      const targetIndex = Math.max(0, Math.min(data.orderIndex - 1, orderedIds.length));
      orderedIds.splice(targetIndex, 0, created.id);
      await reorderStoryboards(data.projectId, orderedIds);
    }

    return created;
  },

  // 更新分镜
  async update(id: string, data: UpdateStoryboardDto): Promise<Storyboard | null> {
    const payload: Record<string, unknown> = {};

    if (data.sceneId !== undefined) payload.sceneId = data.sceneId;
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) {
      payload.description = buildDescriptionPayload(data.description);
    }
    if (data.shootingDescription !== undefined) {
      payload.shootingDescription = data.shootingDescription;
    }
    if (data.customFields !== undefined) {
      payload.customFields = data.customFields;
    }
    // 看板依赖 orderIndex + duration 定位，不再向后端传 startTime/endTime
    if (data.estimatedDuration !== undefined) payload.estimatedDuration = data.estimatedDuration;
    if (data.actualDuration !== undefined) payload.actualDuration = data.actualDuration;
    if (data.orderIndex !== undefined) payload.orderIndex = data.orderIndex;
    if (data.transitionType !== undefined) payload.transitionType = data.transitionType;
    if (data.transitionDuration !== undefined) {
      payload.transitionDuration = data.transitionDuration;
    }

    const response = (await apiClient.put(`/storyboards/${id}`, payload)) as StoryboardApiResponse;

    return normalizeStoryboard(response);
  },

  // 删除分镜
  async batchSave(payload: StoryboardBatchSavePayload): Promise<StoryboardBatchSaveResponse> {
    return (await apiClient.post(
      '/storyboards/batch-save',
      payload
    )) as StoryboardBatchSaveResponse;
  },

  async delete(id: string): Promise<boolean> {
    await apiClient.delete(`/storyboards/${id}`);
    return true;
  },

  // 复制分镜（后端暂无接口，保留占位避免编译报错）
  async duplicate(_id: string): Promise<Storyboard | null> {
    throw new Error('后端暂未提供分镜复制接口');
  },

  // 重新排序分镜
  async reorder(projectId: string, orderedIds: string[]): Promise<void> {
    await reorderStoryboards(projectId, orderedIds);
  },

  // 查询资源关联的分镜列表（elementType: 1=角色 2=场景 3=道具，elementId 为实体 UUID）
  // tagId 可选，精确到某个装扮/布景/状态
  async getLinkedStoryboards(
    projectId: string,
    elementType: number,
    elementId: string,
    tagId?: string
  ): Promise<Storyboard[]> {
    const params: Record<string, string | number> = { projectId, elementType, elementId };
    if (tagId) params.tagId = tagId;
    const response = (await apiClient.get('/storyboards/linked-storyboards', {
      params,
    })) as StoryboardApiResponse[];
    return response.map(normalizeStoryboard);
  },

  async getScene(storyboardId: string): Promise<ScreenplayScene | null> {
    try {
      const response = (await apiClient.get(
        `/storyboards/${storyboardId}/scene`
      )) as ScreenplayScene;
      return response;
    } catch (error: unknown) {
      // 404 = 分镜无关联场次（v2 分镜），属于正常情况返回 null
      if (hasErrorCode(error) && error.code === 404) {
        return null;
      }
      // 其他错误（网络、服务器等）抛出，由 TanStack Query 处理重试/错误态
      throw error;
    }
  },

  // AI 智能提取分镜关联元素（角色/场景/道具/对白），结果直接入库
  async extractElements(storyboardId: string): Promise<StoryboardExtractJobResponse> {
    const response = (await apiClient.post(
      `/storyboards/${storyboardId}/extract-elements`
    )) as StoryboardExtractJobResponse;
    return response;
  },
};
